import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Facility, FleetVehicle, validPosition } from "./fleetData";
import { fleetAtBase, fleetDestination, fleetRouteEndpoints } from "./fleetRoute";
import { LabelBox, placeFleetLabel } from "./fleetLabels";

const center: L.LatLngTuple = [18.94, 73.025];
// Fade the data panes together; leave the basemap and viewport untouched.
function revealFleetUpdate(instance: L.Map) {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  ["markerPane", "tooltipPane", "overlayPane"].forEach(name => {
    const pane = instance.getPane(name);
    if (!pane?.animate) return;
    pane.animate([{ opacity: 0.65 }, { opacity: 1 }], {
      duration: 360, easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    });
  });
}

const colors = { running: "#458e62", idle: "#c07a30", halted: "#bb4340", unknown: "#7a8597" };
interface Props { vehicles: FleetVehicle[]; facilities: Facility[]; selected?: FleetVehicle; onSelect: (id: string) => void; trails: Record<string, L.LatLngTuple[]>; }
export default function FleetMap({ vehicles, facilities, selected, onSelect }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const vehicleLayer = useRef<L.LayerGroup | null>(null);
  const facilityLayer = useRef<L.LayerGroup | null>(null);
  const routeLayer = useRef<L.LayerGroup | null>(null);
  const baseRouteLayer = useRef<L.LayerGroup | null>(null);
  const routeSelection = useRef<string>();
  const [routeNotice, setRouteNotice] = useState("");
  const [routeMetrics, setRouteMetrics] = useState<Record<string, string>>({});

  const routeMetricsRef = useRef(routeMetrics);
  routeMetricsRef.current = routeMetrics;
  const metricLabels = useRef(new Map<string, HTMLElement[]>());
  // Route responses update only their text nodes; they must not rebuild the map.
  useLayoutEffect(() => {
    metricLabels.current.forEach((labels, key) => {
      labels.forEach(label => { label.textContent = routeMetrics[key] || ""; });
    });
    if (map.current && Object.keys(routeMetrics).length) revealFleetUpdate(map.current);
  }, [routeMetrics]);

  const [tileError, setTileError] = useState(false);
  const fitted = useRef(false);
  const previousSelection = useRef<string>();
  useLayoutEffect(() => {
    if (!element.current) return;
    const instance = L.map(element.current, { zoomControl: false, scrollWheelZoom: true }).setView(center, 12);
    map.current = instance;
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).on("tileerror", () => setTileError(true)).addTo(instance);
    L.control.zoom({ position: "bottomright" }).addTo(instance);
    vehicleLayer.current = L.layerGroup().addTo(instance);
    facilityLayer.current = L.layerGroup().addTo(instance);
    routeLayer.current = L.layerGroup().addTo(instance);
    baseRouteLayer.current = L.layerGroup().addTo(instance);
    const observer = new ResizeObserver(() => instance.invalidateSize({ pan: false, debounceMoveend: true }));
    observer.observe(element.current);
    return () => { observer.disconnect(); instance.remove(); map.current = null; fitted.current = false; };
  }, []);
  useLayoutEffect(() => {
    const layer = vehicleLayer.current;
    if (!layer || !map.current) return;
    layer.clearLayers();
    metricLabels.current.clear();
    const instance = map.current;
    const markers: L.Marker[] = [];
    const links = new Map<L.Marker, { color: string; line?: L.Polyline; active: boolean }>();
    const connectors = L.layerGroup().addTo(layer);
    const positions: L.LatLngTuple[] = [];
    vehicles.forEach(vehicle => {
      if (vehicle.lat === undefined || vehicle.lng === undefined) return;
      const position: L.LatLngTuple = [vehicle.lat, vehicle.lng];
      positions.push(position);
      const label = document.createElement("button");
      label.type = "button";
      const number = document.createElement("span");
      number.textContent = vehicle.plate;
      label.appendChild(number);
      if (vehicle.unreachable) {
        const stale = document.createElement("small");
        stale.textContent = "Stale location · last known";
        stale.className = "fleet-stale-badge";
        label.appendChild(stale);
      }
      const speed = document.createElement("small");
      speed.className = "fleet-trailer-route-metrics";
      speed.textContent = (vehicle.telemetry?.metricsStale ? "Stale speed: " : "Speed: ") + ((vehicle.speed ?? vehicle.telemetry?.speed) != null ? (vehicle.speed ?? vehicle.telemetry?.speed) + " km/h" : "Unavailable");
      label.appendChild(speed);
      const endpoints = fleetRouteEndpoints(vehicle, facilities);
      const metric = fleetAtBase(vehicle, facilities) ? (vehicle.unreachable ? "At base station · Last known" : "At base station") : endpoints ? routeMetricsRef.current[JSON.stringify(endpoints)] : undefined;
      // Reserve the distance/ETA row before requests finish, so label boxes do
      // not change size and jump around as individual route responses arrive.
      if (metric || endpoints) {
        const details = document.createElement("small");
        details.className = "fleet-trailer-route-metrics fleet-label-route-value";
        details.textContent = metric || "";
        label.appendChild(details);
        if (endpoints) {
          const key = JSON.stringify(endpoints);
          metricLabels.current.set(key, [...(metricLabels.current.get(key) || []), details]);
        }
      }
      label.setAttribute("aria-label", "Show details for " + vehicle.plate);
      L.DomEvent.disableClickPropagation(label);
      label.addEventListener("click", () => onSelect(vehicle.id));
      const marker = L.marker(position, {
        keyboard: true, title: vehicle.plate + ", " + (vehicle.unreachable ? "Stale location · last known" : vehicle.status),
        icon: L.divIcon({ className: "fleet-vehicle-marker" + (vehicle.unreachable ? " unreachable" : "") + (selected?.id === vehicle.id ? " selected" : ""), html: '<span style="--vehicle-color:' + (vehicle.unreachable ? "#a16207" : colors[vehicle.status]) + '"><i></i></span>', iconSize: [24, 16], iconAnchor: [12, 8] }),
      }).bindTooltip(label, { permanent: true, interactive: true, opacity: 1, direction: "center", className: "fleet-trailer-label", offset: [0, -30] }).on("click", () => onSelect(vehicle.id)).addTo(layer);
      const color = vehicle.unreachable ? "#a16207" : colors[vehicle.status];
      const link = { color, active: false, line: undefined as L.Polyline | undefined };
      links.set(marker, link);
      marker.getTooltip()?.getElement()?.style.setProperty("--trailer-link-color", color);
      marker.getElement()?.style.setProperty("--trailer-link-color", color);
      const highlight = (active: boolean) => {
        link.active = active;
        link.line?.setStyle({ weight: active ? 5 : 3 });
        if (active) link.line?.bringToFront();
        marker.getElement()?.classList.toggle("fleet-link-active", active);
        marker.getTooltip()?.getElement()?.classList.toggle("fleet-link-active", active);
      };
      label.addEventListener("mouseenter", () => highlight(true));
      label.addEventListener("mouseleave", () => highlight(false));
      label.addEventListener("focus", () => highlight(true));
      label.addEventListener("blur", () => highlight(false));
      marker.on("mouseover", () => highlight(true)).on("mouseout", () => highlight(false));
      marker.getElement()?.addEventListener("focus", () => highlight(true));
      marker.getElement()?.addEventListener("blur", () => highlight(false));
      markers.push(marker);
    });
    vehicles.forEach(vehicle => {
      const endpoint = fleetDestination(vehicle, facilities);
      if (endpoint) positions.push([endpoint.lat, endpoint.lng]);
    });
    if ((!fitted.current || (previousSelection.current && !selected)) && positions.length) {
      map.current.fitBounds(L.latLngBounds(positions), { padding: [70, 90], maxZoom: 14 });
      fitted.current = true;
    }
    previousSelection.current = selected?.id;
    const alignLabels = () => {
      connectors.clearLayers();
      const size = instance.getSize();
      const visible = markers.filter(marker => {
        const point = instance.latLngToContainerPoint(marker.getLatLng());
        return point.x >= 0 && point.y >= 0 && point.x <= size.x && point.y <= size.y;
      });
      const occupied: LabelBox[] = visible.map(marker => {
        const point = instance.latLngToContainerPoint(marker.getLatLng());
        return { x: point.x - 16, y: point.y - 12, width: 32, height: 24 };
      });
      visible.forEach(marker => {
        const tooltip = marker.getTooltip();
        const element = tooltip?.getElement();
        if (!tooltip || !element) return;
        const anchor = instance.latLngToContainerPoint(marker.getLatLng());
        const box = placeFleetLabel(anchor, element.offsetWidth, element.offsetHeight, occupied, size);
        occupied.push(box);
        tooltip.options.offset = L.point(box.x + box.width / 2 - anchor.x, box.y + box.height / 2 - anchor.y);
        tooltip.setLatLng(marker.getLatLng());
        const end = L.point(Math.max(box.x, Math.min(anchor.x, box.x + box.width)), Math.max(box.y, Math.min(anchor.y, box.y + box.height)));
        const link = links.get(marker)!;
        const path = [marker.getLatLng(), instance.containerPointToLatLng(end)];
        L.polyline(path, { color: "#fff", weight: 7, opacity: 1, interactive: false }).addTo(connectors);
        link.line = L.polyline(path, {
          color: link.color, weight: link.active ? 5 : 3, opacity: 1, interactive: false,
        }).addTo(connectors);
        L.circleMarker(marker.getLatLng(), { radius: 4, color: "#fff", weight: 2, fillColor: link.color, fillOpacity: 1, interactive: false }).addTo(connectors);
      });
    };
    alignLabels();
    if (markers.length) revealFleetUpdate(instance);
    instance.on("zoomend moveend resize", alignLabels);
    return () => { instance.off("zoomend moveend resize", alignLabels); };
  }, [vehicles, facilities, selected?.id, onSelect]);
  useEffect(() => {
    const layer = facilityLayer.current;
    if (!layer) return;
    layer.clearLayers();
    const endpoints = new Map<string, ReturnType<typeof fleetDestination>>();
    vehicles.forEach(vehicle => {
      const endpoint = fleetDestination(vehicle, facilities);
      if (endpoint) endpoints.set(`${endpoint.kind}:${endpoint.lat}:${endpoint.lng}`, endpoint);
    });
    endpoints.forEach(endpoint => {
      if (!endpoint) return;
      const label = document.createElement("span");
      label.textContent = (endpoint.kind === "base" ? "Base · " : "Destination · ") + endpoint.name;
      L.circleMarker([endpoint.lat, endpoint.lng], { radius: endpoint.kind === "base" ? 9 : 6, color: "#fff", weight: 2, fillColor: endpoint.kind === "base" ? "#6c619a" : "#1062b9", fillOpacity: 1 })
        .bindTooltip(label, { permanent: endpoint.kind === "base" || Boolean(selected), direction: "right", className: "fleet-facility-label" }).addTo(layer);
    });
  }, [facilities, vehicles, selected]);
  const baseRoutesKey = JSON.stringify(vehicles.flatMap(vehicle => {
    const endpoints = fleetRouteEndpoints(vehicle, facilities);
    return endpoints?.[1].kind === "base" ? [{ plate: vehicle.plate, endpoints }] : [];
  }));
  useEffect(() => {
    const layer = baseRouteLayer.current;
    if (!layer) return;
    // Keep the current routes visible while the next group is prepared.
    const pendingLayer = L.layerGroup();
    const pendingMetrics: Record<string, string> = {};
    const candidates: { plate: string; endpoints: NonNullable<ReturnType<typeof fleetRouteEndpoints>> }[] = JSON.parse(baseRoutesKey);
    const controller = new AbortController();
    let active = true;
    let cursor = 0;
    const routingUrl = (process.env.REACT_APP_FLEET_ROUTING_URL || "https://router.project-osrm.org").replace(/\/$/, "");
    const load = async () => {
      while (active && cursor < candidates.length) {
        const { endpoints: routeEndpoints } = candidates[cursor++];
        const [start, end] = routeEndpoints;
        const request = new AbortController();
        const cancel = () => request.abort();
        controller.signal.addEventListener("abort", cancel, { once: true });
        const timeout = setTimeout(cancel, 15000);
        try {
          const response = await fetch(`${routingUrl}/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson`, { signal: request.signal });
          if (!response.ok) throw new Error("Route request failed");
          const data = await response.json();
          const route = data.routes?.[0];
          if (data.code !== "Ok" || !Array.isArray(route?.geometry?.coordinates) || route.geometry.coordinates.length < 2 || !Number.isFinite(route.distance) || !Number.isFinite(route.duration) || route.distance < 0 || route.duration < 0) throw new Error("No road route");
          const points: L.LatLngTuple[] = route.geometry.coordinates.map((point: number[]) => {
            const position = validPosition(point[1], point[0]);
            if (!position) throw new Error("Invalid route");
            return [position.lat, position.lng];
          });
          if (!active) return;
          const metric = (route.distance / 1000).toFixed(1) + " km · ~" + Math.ceil(route.duration / 60) + " min";
          pendingMetrics[JSON.stringify(routeEndpoints)] = metric;
          L.polyline(points, { color: "#fff", weight: 8, opacity: .9, interactive: false }).addTo(pendingLayer);
          L.polyline(points, { color: "#1062b9", weight: 5, opacity: 1 }).addTo(pendingLayer);
        } catch {
          // Leave unavailable routes absent rather than adding extra map labels.
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener("abort", cancel);
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(4, candidates.length) }, load)).then(() => {
      if (!active) return;
      // Commit one completed group, rather than flashing each route as it arrives.
      layer.clearLayers();
      pendingLayer.addTo(layer);
      setRouteMetrics(current => ({ ...current, ...pendingMetrics }));
    });
    return () => { active = false; controller.abort(); };
  }, [baseRoutesKey]);
  const hasSelection = Boolean(selected);
  useEffect(() => {
    const instance = map.current;
    const layer = baseRouteLayer.current;
    if (!instance || !layer) return;
    if (hasSelection) instance.removeLayer(layer);
    else if (!instance.hasLayer(layer)) layer.addTo(instance);
  }, [hasSelection]);
  const endpoints = selected ? fleetRouteEndpoints(selected, facilities) : undefined;
  const startLat = endpoints?.[0].lat, startLng = endpoints?.[0].lng;
  const endLat = endpoints?.[1].lat, endLng = endpoints?.[1].lng;
  const startName = endpoints?.[0].name;
  const endKind = endpoints?.[1].kind;
  const selectedRouteKey = endpoints ? JSON.stringify(endpoints) : undefined;
  const selectedId = selected?.id;
  const selectedAtBase = selected ? fleetAtBase(selected, facilities) : false;
  const selectedUnreachable = selected?.unreachable;
  useEffect(() => {
    const layer = routeLayer.current;
    if (!layer || !map.current) return;
    layer.clearLayers();
    setRouteNotice("");
    if (!selectedId) { routeSelection.current = undefined; return; }
    if (selectedAtBase) { setRouteNotice(selectedUnreachable ? "At base station · Last known position within 500 m" : "At base station · Within 500 m"); return; }
    if (startLat === undefined || startLng === undefined || endLat === undefined || endLng === undefined) {
      setRouteNotice("Route unavailable: source, destination or base coordinates are missing.");
      return;
    }
    const label = document.createElement("span"); label.textContent = "Source · " + startName;
    if (endKind !== "base") {
    L.circleMarker([startLat, startLng], { radius: 6, color: "#fff", weight: 2, fillColor: "#458e62", fillOpacity: 1 })
      .bindTooltip(label, { permanent: true, direction: "right", className: "fleet-facility-label" }).addTo(layer);
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let active = true;
    setRouteNotice("Loading road route…");
    const routingUrl = process.env.REACT_APP_FLEET_ROUTING_URL || "https://router.project-osrm.org";
    fetch(`${routingUrl.replace(/\/$/, "")}/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?overview=full&geometries=geojson`, { signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error("Route request failed");
        const data = await response.json();
        const route = data.routes?.[0];
        if (data.code !== "Ok" || !Array.isArray(route?.geometry?.coordinates) || route.geometry.coordinates.length < 2) throw new Error("No road route");
        const points: L.LatLngTuple[] = route.geometry.coordinates.map((point: number[]) => {
          const position = validPosition(point[1], point[0]);
          if (!position) throw new Error("Invalid route");
          return [position.lat, position.lng];
        });
        if (!active) return;
        L.polyline(points, { color: "#fff", weight: 8, opacity: .85 }).addTo(layer);
        const metric = (route.distance / 1000).toFixed(1) + " km" +
          (Number.isFinite(route.duration) && route.duration >= 0 ? " · ~" + Math.ceil(route.duration / 60) + " min" : "");
        if (selectedRouteKey) setRouteMetrics(current => current[selectedRouteKey] === metric ? current : { ...current, [selectedRouteKey]: metric });
        L.polyline(points, { color: "#1062b9", weight: 6, opacity: 1 }).addTo(layer);
        if (routeSelection.current !== selectedId) {
          map.current?.fitBounds(L.latLngBounds(points), { padding: [80, 90], maxZoom: 15 });
          routeSelection.current = selectedId;
        }
        const duration = typeof route.duration === "number" && Number.isFinite(route.duration) && route.duration >= 0
          ? " · Est. " + Math.ceil(route.duration / 60) + " min" : "";
        setRouteNotice("Road route · " + (route.distance / 1000).toFixed(1) + " km" + duration + " · Traffic and truck restrictions not included");
      })
      .catch(() => { if (active) setRouteNotice("Road route unavailable. Please select the truck again to retry."); })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); layer.clearLayers(); };
  }, [selectedId, startLat, startLng, endLat, endLng, startName, endKind, selectedRouteKey, selectedAtBase, selectedUnreachable]);
  useEffect(() => {
    if (selected?.lat !== undefined && selected.lng !== undefined) map.current?.panTo([selected.lat, selected.lng]);
  }, [selected?.id, selected?.lat, selected?.lng]);
  const fit = () => {
    const points = vehicles.filter(v => v.lat !== undefined && v.lng !== undefined).map(v => [v.lat!, v.lng!] as L.LatLngTuple);
    vehicles.forEach(vehicle => {
      const endpoint = fleetDestination(vehicle, facilities);
      if (endpoint) points.push([endpoint.lat, endpoint.lng]);
    });
    if (points.length) map.current?.fitBounds(L.latLngBounds(points), { padding: [75, 90], maxZoom: 14 });
    else map.current?.setView(center, 12);
  };
  return <>
    <div ref={element} className="fleet-map-canvas" aria-label="Live fleet map" />
    {routeNotice && <div className="fleet-route-notice" role="status">{routeNotice}</div>}
    {tileError && <div className="fleet-map-notice">Map tiles are unavailable. Vehicle lists remain accessible.</div>}
    <div className="fleet-map-tools" role="toolbar" aria-label="Map tools">
      <button onClick={fit} aria-label="Show the whole fleet" title="Show the whole fleet">⛶</button>
    </div>
    <div className="fleet-map-legend"><span><i className="running" />Running</span><span><i className="idle" />Idle</span><span><i className="halted" />Halted</span><span><i className="unknown" />Unreachable / unknown</span><span>Base routes · Times exclude traffic and truck restrictions</span></div>
  </>;
}
